import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2 } from "lucide-react";
import sumupCardReader from "@/assets/sumup-card-reader.png";
import sumupPrinter from "@/assets/sumup-printer.png";

const Partners = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAdmin = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        navigate("/auth");
        return;
      }

      const { data: roleData } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .eq("role", "site_admin")
        .maybeSingle();

      if (!roleData) {
        navigate("/");
        return;
      }

      setLoading(false);
    };

    checkAdmin();
  }, [navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12">
      <div className="container max-w-6xl">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Strategic Partners
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            We collaborate with industry-leading partners to bring exclusive benefits and discounts to PTA Hub users.
          </p>
        </div>

        {/* Sum Up Partnership */}
        <Card className="overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-primary/10 to-primary/5">
            <div className="flex items-center justify-between mb-4">
              <CardTitle className="text-3xl">SumUp</CardTitle>
              <Badge variant="default" className="text-sm">
                Exclusive Partner Discount
              </Badge>
            </div>
            <CardDescription className="text-lg">
              Accept card payments anywhere with market-leading card readers and payment solutions
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div>
                <h3 className="text-2xl font-semibold mb-4">Partnership Benefits</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <span><strong>Exclusive Hardware Discounts:</strong> Save on SumUp Solo card readers and receipt printers</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <span><strong>Reduced Transaction Fees:</strong> Special partner rates on all card transactions</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <span><strong>No Monthly Fees:</strong> Pay-as-you-go pricing with no hidden costs</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <span><strong>Next-Day Settlements:</strong> Fast access to your funds</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <span><strong>Accept All Card Types:</strong> Contactless, chip & PIN, magnetic stripe, Apple Pay & Google Pay</span>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-2xl font-semibold mb-4">Perfect For PTAs</h3>
                <p className="text-muted-foreground mb-4">
                  SumUp's portable card readers are ideal for school events, fundraisers, and on-the-go sales. The compact Solo reader connects to your smartphone via Bluetooth, making it perfect for:
                </p>
                <ul className="space-y-2 text-muted-foreground">
                  <li>• School fairs and summer fetes</li>
                  <li>• Bake sales and refreshment stands</li>
                  <li>• Uniform sales and second-hand shops</li>
                  <li>• Raffle and event ticket sales</li>
                  <li>• Fundraising campaigns</li>
                  <li>• Parent evening bookstalls</li>
                </ul>
              </div>
            </div>

            {/* Product Showcase */}
            <div className="border-t pt-8">
              <h3 className="text-2xl font-semibold mb-6 text-center">Featured Products</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>SumUp Solo Card Reader</CardTitle>
                    <CardDescription>Compact, portable payment terminal</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <img 
                      src={sumupCardReader} 
                      alt="SumUp Solo card reader" 
                      className="w-full h-64 object-cover rounded-lg mb-4"
                    />
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li>✓ Bluetooth connectivity</li>
                      <li>✓ 500+ transactions per charge</li>
                      <li>✓ Contactless, chip & PIN</li>
                      <li>✓ Works with iOS & Android</li>
                    </ul>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>SumUp Receipt Printer</CardTitle>
                    <CardDescription>Professional thermal printer</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <img 
                      src={sumupPrinter} 
                      alt="SumUp receipt printer" 
                      className="w-full h-64 object-cover rounded-lg mb-4"
                    />
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li>✓ Portable & rechargeable</li>
                      <li>✓ Bluetooth connectivity</li>
                      <li>✓ Up to 200 receipts per charge</li>
                      <li>✓ Pairs with SumUp card readers</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Call to Action */}
            <div className="mt-8 p-6 bg-primary/5 rounded-lg text-center">
              <h3 className="text-xl font-semibold mb-2">Interested in Partner Discounts?</h3>
              <p className="text-muted-foreground mb-4">
                Contact our partnerships team to learn more about exclusive pricing and getting started with SumUp.
              </p>
              <Badge variant="outline" className="text-sm">
                More details coming soon
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Partners;
